import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useBo } from "./AdminAuth";
import { errMsg } from "./api";
import { Spinner } from "./ui";

function Field({ f, value, onChange }) {
  const id = `fld-${f.key}`;
  if (f.type === "select") {
    return (
      <select id={id} data-testid={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
        {(f.options || []).map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
      </select>
    );
  }
  if (f.type === "multi") {
    const cur = value || [];
    return (
      <div className="flex flex-wrap gap-2" data-testid={id}>
        {f.options.map((o) => (
          <label key={o} className="flex items-center gap-1.5 rounded border border-border px-2 py-1 text-xs">
            <input type="checkbox" checked={cur.includes(o)} onChange={(e) => onChange(e.target.checked ? [...cur, o] : cur.filter((x) => x !== o))} />{o}
          </label>
        ))}
      </div>
    );
  }
  if (f.type === "checkbox") {
    return <input id={id} data-testid={id} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />;
  }
  if (f.type === "textarea") return <Textarea id={id} data-testid={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={4} />;
  return <Input id={id} data-testid={id} type={f.type || "text"} value={value ?? ""} placeholder={f.placeholder} onChange={(e) => onChange(f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} />;
}

export function ActionDialog({ open, onOpenChange, title, description, changes, fields = [], initial = {}, onSubmit, danger, noReason, testid }) {
  const [vals, setVals] = useState(initial);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!noReason && reason.trim().length < 3) return toast.error("A reason is required");
    setBusy(true);
    try {
      const res = await onSubmit({ ...vals }, reason.trim());
      if (res?.pending_approval) toast.info(res.message || "Sent for approval");
      else toast.success(res?.message || "Done");
      onOpenChange(false);
      setReason("");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={testid || "action-dialog"} className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {changes?.length > 0 && (
          <div className="rounded-md border border-border bg-muted/40 p-3 text-xs" data-testid="action-changes">
            <div className="mb-1.5 font-semibold uppercase tracking-wider text-muted-foreground">What will change</div>
            {changes.map((c, i) => (
              <div key={i} className="flex justify-between gap-3 py-0.5"><span className="text-muted-foreground">{c.label}</span>
                <span className="num">{c.from !== undefined && <><span className="line-through opacity-60">{String(c.from)}</span> → </>}<b>{String(c.to)}</b></span></div>
            ))}
          </div>
        )}
        <div className="space-y-3">
          {fields.map((f) => (
            <div key={f.key} className="space-y-1">
              <label htmlFor={`fld-${f.key}`} className="text-xs font-medium text-muted-foreground">{f.label}</label>
              <Field f={f} value={vals[f.key]} onChange={(v) => setVals((s) => ({ ...s, [f.key]: v }))} />
            </div>
          ))}
          {!noReason && (
            <div className="space-y-1">
              <label htmlFor="action-reason" className="text-xs font-medium text-muted-foreground">Reason (required, saved in the audit log)</label>
              <Textarea id="action-reason" data-testid="action-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="action-cancel-btn">Cancel</Button>
          <Button data-testid="action-confirm-btn" variant={danger ? "destructive" : "default"} onClick={submit} disabled={busy}>
            {busy && <Spinner className="mr-1.5 text-current" />}Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ActionButton({ perm, label, icon: Icon, variant = "outline", size = "sm", testid, onDone, ...dialog }) {
  const { can } = useBo();
  const [open, setOpen] = useState(false);
  if (perm && !can(perm)) return null;
  const submit = async (v, r) => {
    const res = await dialog.onSubmit(v, r);
    onDone?.(res);
    return res;
  };
  return (
    <>
      <Button data-testid={testid} variant={variant} size={size} onClick={(e) => { e.stopPropagation(); setOpen(true); }} className="h-7 gap-1 px-2 text-xs">
        {Icon && <Icon className="h-3.5 w-3.5" />}{label}
      </Button>
      {open && <ActionDialog {...dialog} title={dialog.title || label} open={open} onOpenChange={setOpen} onSubmit={submit} />}
    </>
  );
}
